import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import classNames from "classnames"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router"
import { FacilityList } from "./FacilityList"
import NewCustomer from "./NewCustomer"
import NewFacility from "./NewFacility"
import { SingleCustomer } from "./SingleCustomer"
import SingleFacility from "./SingleFacilityCustomer"
import { Header } from "@/api"
import { isEditingAnotherRow } from "@/components/CustomersFacilitiesTable/utils"
import { Icon, IconColor } from "@/components/shared/Icon"
import { SortDirection } from "@/components/shared/Table/types"
import UserModal from "@/components/UserModal"
import { PageRoutes } from "@/constants/routes"
import { UserFormData } from "@/pages/Settings/Users/types"
import { AppDispatch, RootState } from "@/store"
import { createUser } from "@/store/usersSlice"
import { CustomerForm } from "@/types/customer"
import { Facility, FacilityForm } from "@/types/facility"
import { ColorVariant, Token } from "@/types/index"
import { resetAllFacilityData } from "@/utils/facilities"
import { cleanDeep, sortByField } from "@/utils/index"
import { formatUserData } from "@/utils/users"
import "./index.scss"

export interface Customer {
   id: string
   customerName: string
   state: string
   facilities: Facility[]
   users: number
   cells: number
   wearables: number
}

interface CustomersFacilitiesTableProps {
   filteredData: Customer[]
   isEmptyData: boolean
   error: string | undefined
   createCustomerMode: boolean
   createFacilityMode: boolean
   rowDeletionSuccess: boolean
   cancelCustomerCreation: () => void
   cancelFacilityCreation: () => void
   onCreateFacility: () => void
   onSaveCustomer: (customerForm: CustomerForm) => void
   onAddFacility: (customerId: string, facilityForm: FacilityForm) => void
   onDeleteCustomer: (customerId: string, customerName: string) => void
   onDeleteFacility: (facilityId: string, facilityName: string) => void
   onEditRowChange: (isEditing: boolean) => void
}

const CustomersFacilitiesTable: FC<CustomersFacilitiesTableProps> = ({
   filteredData,
   isEmptyData,
   error,
   createCustomerMode,
   createFacilityMode,
   rowDeletionSuccess,
   cancelCustomerCreation,
   cancelFacilityCreation,
   onCreateFacility,
   onSaveCustomer,
   onAddFacility,
   onDeleteCustomer,
   onDeleteFacility,
   onEditRowChange,
}) => {
   const navigate = useNavigate()
   const dispatch = useDispatch<AppDispatch>()
   const tableRef = useRef<HTMLDivElement>(null)
   const [editModeActiveRow, setEditActiveRow] = useState<string>("")
   const [deleteActiveRow, setDeleteActiveRow] = useState<string>("")
   const [createActiveCustomer, setCreateActiveCustomer] = useState<string>("")
   const [order, setOrder] = useState<SortDirection>(SortDirection.ASC)
   const [orderBy, setOrderBy] = useState<keyof Customer | null>("customerName")
   const [savedUser, setSavedUser] = useState<UserFormData | null>(null)
   const [userFacilityId, setUserFacilityId] = useState<string | null>(null)
   const [openUserModal, setOpenUserModal] = useState(false)
   const { errorCreateUser } = useSelector(
      ({ usersReducer }: RootState) => usersReducer
   )

   const handleSort = useCallback(
      (property: keyof Customer) => {
         const isAsc = orderBy === property && order === SortDirection.ASC
         setOrder(isAsc ? SortDirection.DESC : SortDirection.ASC)
         setOrderBy(property)
      },
      [order, orderBy]
   )

   const isFilteredDataEmpty = useMemo(() => {
      return filteredData?.length === 0
   }, [filteredData])

   const userFacilityName: string = useMemo(() => {
      if (!userFacilityId) {
         return ""
      }
      const facility = filteredData
         .flatMap((customer) => customer.facilities)
         .find((facility) => facility.id === userFacilityId)
      return facility?.name || ""
   }, [userFacilityId, filteredData])

   const handleSave = useCallback(
      (customerForm: CustomerForm) => {
         const filteredCustomerForm = cleanDeep(
            customerForm as CustomerForm
         ) as CustomerForm
         onSaveCustomer(filteredCustomerForm)
      },
      [onSaveCustomer]
   )

   const handleAddFacility = (
      customerId: string,
      facilityForm: FacilityForm
   ) => {
      const filteredFacilityForm = cleanDeep(facilityForm) as FacilityForm
      onAddFacility(customerId, filteredFacilityForm)
   }

   const sortedRows: Customer[] = useMemo(() => {
      const customersWithSortedFacilities = filteredData.map((customer) => ({
         ...customer,
         facilities: sortByField(customer.facilities, "name", order),
      }))
      return sortByField(customersWithSortedFacilities, "customerName", order)
   }, [filteredData, order])

   const scrollToNewCustomer = () => {
      setTimeout(() => {
         const newCustomerElements = document.querySelectorAll(
            ".customers-facilities-table__group--single-facility"
         )
         if (newCustomerElements.length > 0 && tableRef.current) {
            const newCustomerElement =
               newCustomerElements[newCustomerElements.length - 1]
            newCustomerElement.scrollIntoView({
               behavior: "smooth",
               block: "center",
            })
         }
      }, 100)
   }

   useEffect(() => {
      onEditRowChange(editModeActiveRow !== "")
   }, [editModeActiveRow, onEditRowChange])

   useEffect(() => {
      if (createCustomerMode) {
         setEditActiveRow("")
         scrollToNewCustomer()
      }
   }, [createCustomerMode])

   useEffect(() => {
      if (rowDeletionSuccess) {
         setEditActiveRow("")
      }
   }, [rowDeletionSuccess])

   const handleFacilityClick = (customerId: string, facilityId: string) => {
      resetAllFacilityData(dispatch)
      sessionStorage.setItem(Token.CUSTOMER_ID, customerId)
      sessionStorage.setItem(Token.FACILITY_ID, facilityId)
      navigate(PageRoutes.FLOOR_OVERVIEW)
   }

   const handleAddUser = (facilityId: string) => {
      setUserFacilityId(facilityId)
      setOpenUserModal(true)
   }

   const handleCloseUserModal = () => {
      setOpenUserModal(false)
      setSavedUser(null)
      setUserFacilityId(null)
      setEditActiveRow("")
   }

   const handleSaveUser = (data: UserFormData) => {
      if (!userFacilityId) {
         return
      }
      const formattedUserData = formatUserData(data)
      dispatch(
         createUser({
            header: Header.FACILITY_ID,
            userData: formattedUserData,
            headerId: userFacilityId,
         })
      )
         .unwrap()
         .then(() => {
            setSavedUser(data)
         })
         .catch((error) => {
            console.error("Error adding user:", error)
         })
   }

   return (
      <>
         <div
            className="customers-facilities-table"
            ref={tableRef}
         >
            <div className="customers-facilities-table__header-wrapper">
               <div
                  className="customers-facilities-table__header"
                  onClick={() => handleSort("customerName")}
               >
                  <span>Customers and Facilities</span>
                  <Icon
                     name="sortBy"
                     color={IconColor.HONOLULU_BLUE}
                     size="13"
                  />
               </div>
            </div>
            {!isFilteredDataEmpty &&
               sortedRows.map((sortedRow, index) => {
                  const {
                     id,
                     customerName,
                     facilities,
                     cells,
                     wearables,
                     users,
                  } = sortedRow
                  const customerItem = {
                     customerName,
                     cells,
                     wearables,
                     users,
                  }
                  return (
                     <div
                        key={`${id}-${index}`}
                        className="customers-facilities-table__group"
                     >
                        {facilities.length === 1 ? (
                           <>
                              <SingleFacility
                                 key={`${id}-single-facility-item`}
                                 isDisabled={
                                    createCustomerMode ||
                                    createFacilityMode ||
                                    isEditingAnotherRow(editModeActiveRow, id)
                                 }
                                 id={id}
                                 customerItem={sortedRow}
                                 editModeActiveRow={editModeActiveRow}
                                 deleteActiveRow={deleteActiveRow}
                                 onAddUser={(id) => handleAddUser(id)}
                                 onEditModeChange={(id) => setEditActiveRow(id)}
                                 onDelete={() => {
                                    setDeleteActiveRow(id)
                                    onDeleteCustomer(id, customerName)
                                 }}
                                 onCreateFacility={() => {
                                    setEditActiveRow("")
                                    setCreateActiveCustomer(id)
                                    onCreateFacility()
                                 }}
                              />
                              {createFacilityMode &&
                                 createActiveCustomer === id && (
                                    <NewFacility
                                       onSave={(facilityForm) =>
                                          handleAddFacility(id, facilityForm)
                                       }
                                       cancel={() => cancelFacilityCreation()}
                                    />
                                 )}
                           </>
                        ) : (
                           <>
                              <SingleCustomer
                                 key={`${id}-customer-item`}
                                 isDisabled={
                                    createCustomerMode ||
                                    createFacilityMode ||
                                    isEditingAnotherRow(editModeActiveRow, id)
                                 }
                                 id={id}
                                 customerItem={customerItem}
                                 editModeActiveRow={editModeActiveRow}
                                 onEditModeChange={(id) => setEditActiveRow(id)}
                                 onCreateFacility={() => {
                                    setEditActiveRow("")
                                    setCreateActiveCustomer(id)
                                    onCreateFacility()
                                 }}
                                 onDelete={() => {
                                    setDeleteActiveRow(id)
                                    onDeleteCustomer(id, customerName)
                                 }}
                              />
                              <FacilityList
                                 key={`${id}-facility-item`}
                                 createFacilityMode={
                                    createFacilityMode &&
                                    createActiveCustomer === id
                                 }
                                 creationMode={
                                    createCustomerMode || createFacilityMode
                                 }
                                 facilities={facilities}
                                 editModeActiveRow={editModeActiveRow}
                                 deleteActiveRow={deleteActiveRow}
                                 onFacilityClick={(facilityId) =>
                                    handleFacilityClick(id, facilityId)
                                 }
                                 onAddUser={(id) => handleAddUser(id)}
                                 onEditModeChange={(id) => setEditActiveRow(id)}
                                 onDelete={(facilityId, facilityName) => {
                                    setDeleteActiveRow(facilityId)
                                    onDeleteFacility(facilityId, facilityName)
                                 }}
                                 onCreateFacility={() => {
                                    setEditActiveRow("")
                                    setCreateActiveCustomer(id)
                                    onCreateFacility()
                                 }}
                                 cancel={cancelFacilityCreation}
                                 onSave={(facilityForm) =>
                                    handleAddFacility(id, facilityForm)
                                 }
                              />
                           </>
                        )}
                     </div>
                  )
               })}
            {isFilteredDataEmpty && !createCustomerMode && (
               <div
                  className={classNames("customers-facilities-table__message", {
                     "customers-facilities-table__message--error": !!error,
                  })}
               >
                  {error
                     ? error
                     : isEmptyData
                       ? "This table has no entries yet"
                       : "No results found for your search"}
               </div>
            )}
            {createCustomerMode && (
               <NewCustomer
                  onSave={(customerForm) => handleSave(customerForm)}
                  cancel={() => cancelCustomerCreation()}
               />
            )}
         </div>
         {openUserModal && (
            <UserModal
               openModal={openUserModal}
               handleCloseModal={handleCloseUserModal}
               errorCreateUser={errorCreateUser}
               savedUser={savedUser}
               handleSaveUser={handleSaveUser}
               variant={ColorVariant.COOL_GRAY}
               facilityName={userFacilityName}
            />
         )}
      </>
   )
}

export default CustomersFacilitiesTable
